"use client";

import Link from "next/link";
import { ArrowRight, Shield, Activity, Users, Layers, Languages, Radio, FileCheck, Eye } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { trust as data } from "@/lib/data/about";

const recordAccents = [
  "var(--color-emerald)",
  "var(--color-cyan)",
  "var(--color-purple)",
  "var(--color-amber)",
  "var(--color-crimson)",
] as const;
const recordIcons = [Shield, Activity, Users, Layers, Languages] as const;
const commitIcons = [Radio, FileCheck, Eye] as const;

export function Trust() {
  return (
    <Section id="trust" className="relative">
      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-emerald)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {data.trackRecord.map((r, i) => {
            const Icon = recordIcons[i % recordIcons.length]!;
            const accent = recordAccents[i % recordAccents.length]!;
            return (
              <Reveal key={r.label} delay={i * 60}>
                <div
                  className="glass rounded-2xl p-5 h-full"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 12%, transparent)`,
                  }}
                >
                  <Icon className="h-4 w-4 mb-3" style={{ color: accent }} />
                  <div className="text-2xl font-bold" style={{ color: accent }}>
                    {r.value}
                  </div>
                  <div className="text-sm font-semibold mt-2">{r.label}</div>
                  <div className="text-xs text-[rgb(var(--fg-muted))] mt-1 leading-snug">{r.detail}</div>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={400}>
          <div className="mt-10 grid sm:grid-cols-3 gap-5">
            {data.transparencyCommitments.map((c, i) => {
              const Icon = commitIcons[i % commitIcons.length]!;
              return (
                <div key={c.heading} className="glass rounded-2xl p-6">
                  <span
                    className="grid place-items-center h-10 w-10 rounded-lg"
                    style={{
                      background: "color-mix(in oklch, var(--color-emerald) 18%, transparent)",
                      boxShadow: "inset 0 0 0 1px color-mix(in oklch, var(--color-emerald) 35%, transparent)",
                    }}
                  >
                    <Icon className="h-4 w-4 text-[var(--color-emerald)]" />
                  </span>
                  <h3 className="mt-3 text-base font-bold">{c.heading}</h3>
                  <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{c.body}</p>
                </div>
              );
            })}
          </div>
        </Reveal>

        <Reveal delay={500}>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link
                key={c.label}
                href={c.href}
                className={
                  i === 0
                    ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                    : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                }
              >
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
